<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">
      <div v-for="provider in singleprovider" v-bind:key="provider.id">

        <!-- ── Page Header ───────────────────────────────────── -->
        <div class="page-header">
          <div class="container">
            <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-home" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">{{ provider.agency_name }}</h4>
                <div class="d-flex align-items-center mt-1" style="gap: 8px;">
                  <span v-if="provider.status == true" class="header-status header-status--active">
                    <i class="fe fe-check-circle mr-1"></i> Approved
                  </span>
                  <span v-if="provider.status == false" class="header-status header-status--inactive">
                    <i class="fe fe-clock mr-1"></i> Pending
                  </span>
                  <span v-if="provider.phc_type" class="header-id-badge">{{ provider.phc_type }}</span>
                  <span v-if="provider.phc_general" class="header-id-badge">{{ provider.phc_general }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="container mt-n3 pb-5">

          <!-- ── Action Bar ──────────────────────────────────── -->
          <div class="card action-card shadow mb-4">
            <div class="card-body py-3 px-4 d-flex flex-wrap" style="gap: 8px;">
              <button class="btn btn-sm btn-outline-success" @click="acceptProvider(1)" v-if="provider.status == false">
                <i class="fe fe-check-circle mr-1"></i> Approve
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="acceptProvider(0)" v-if="provider.status == true">
                <i class="fe fe-x-circle mr-1"></i> Disapprove
              </button>
              <router-link :to="{ path: '/provider/add-employee/' + provider.id }">
                <button class="btn btn-sm btn-outline-primary">
                  <i class="fe fe-users mr-1"></i> Manage Personnel
                </button>
              </router-link>
              <router-link :to="{ path: '/payout-account', query: { provider_id: provider.id } }">
                <button class="btn btn-sm btn-outline-success">
                  <i class="fe fe-credit-card mr-1"></i> Payout Account
                </button>
              </router-link>
              <router-link :to="{ path: '/edit-provider/' + provider.id }">
                <button class="btn btn-sm btn-outline-info">
                  <i class="fe fe-edit-2 mr-1"></i> Edit Facility
                </button>
              </router-link>
              <button class="btn btn-sm btn-outline-dark" @click="getAIComments"
                data-toggle="modal" data-target="#slideRightModalAll">
                <i class="fe fe-message-circle mr-1"></i> Reviews
              </button>
              <a class="btn btn-sm btn-outline-secondary" :href="`/create-wallet/${provider.id}`">
                <i class="fe fe-credit-card mr-1"></i> Create Wallet
              </a>
            </div>
          </div>

          <!-- Specializations (shared medical list; shown when booking appointments / telemedicine) -->
          <SpecializationsCard v-if="provider.id" :user-id="provider.id"
            :editable="!!user && (user.type == 'shis' || user.type == 'employee')" />

          <!-- ── Stats Tiles ─────────────────────────────────── -->
          <div class="row mb-4">
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Total Enrollees</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.enrollees | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Principal</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.principal | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Dependents</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.dependants | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Encounters</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.encounters | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Claims</p>
                  <router-link :to="{ path: '/all-claims' }">
                    <h3 class="mb-0 font-weight-bold">{{ dashboarddata.claims | numeral("0,0") }}</h3>
                  </router-link>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Out-going Referrals</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.outgoing_referrals | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">In-coming Referrals</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.incoming_referrals | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Employees</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.employees | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Total Utilization Rate</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.encounter_rate_total | numeral("0,0.00") }}%</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Total Referral Rate</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.referral_rate_total | numeral("0,0.00") }}%</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Utilization Rate (Current Month)</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.encounter_rate_current_month | numeral("0,0.00") }}%</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Referral Rate (Current Month)</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.referral_rate_current_month | numeral("0,0.00") }}%</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Total Claim Payment</p>
                  <h3 class="mb-0 font-weight-bold"><i class="mdi mdi-currency-ngn"></i>{{ 0 | numeral("0,0.00") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Total Enrollee Utilization</p>
                  <h3 class="mb-0 font-weight-bold"><i class="mdi mdi-currency-ngn"></i>{{ 0 | numeral("0,0.00") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Reviews</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.reviews | numeral("0,0") }}</h3>
                </div>
              </div>
            </div>
            <div class="col-lg-3 col-md-4 col-6 mb-3">
              <div class="card border-0 shadow-sm h-100">
                <div class="card-body py-3 px-3">
                  <p class="text-muted mb-1 text-uppercase" style="font-size:.7rem;letter-spacing:.05em;">Avg. Rating</p>
                  <h3 class="mb-0 font-weight-bold">{{ dashboarddata.average_rating | numeral("0.00") }}</h3>
                </div>
              </div>
            </div>
          </div>
          <!-- ── Facility performance (time-based, ranked against the agency) ── -->
          <FacilityAnalytics v-if="canSeeAnalytics" :facility-id="$route.params.id" :key="'fa-' + $route.params.id" />


          <!-- ── Enrollees Table + Facility Details ──────────── -->
          <div class="row">
            <div class="col-lg-8 col-md-8">
              <div class="card shadow table-card mb-4">
                <div class="px-4 pt-4 pb-2">
                  <h6 class="mb-3 font-weight-bold text-dark">
                    <i class="fe fe-users text-primary mr-2"></i> Facility Enrollees
                  </h6>
                </div>
                <div class="table-responsive">
                  <table class="table mb-0">
                    <thead class="table-head">
                      <tr>
                        <th class="table-th px-4">Name</th>
                        <th class="table-th">KGSHIA ID</th>
                        <th class="table-th">Contact</th>
                        <th class="table-th">Status</th>
                        <th class="table-th">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="enrollee in providerclients" v-bind:key="enrollee.id" class="table-row">
                        <td class="px-4 py-3">
                          <router-link :to="{ path: '/client/' + enrollee.id }" class="font-weight-bold" style="font-size:.88rem;">
                            {{ enrollee.firstname }} {{ enrollee.lastname }}
                          </router-link>
                        </td>
                        <td class="py-3">
                          <span class="code-chip">{{ enrollee.id_card_number }}</span>
                        </td>
                        <td class="py-3" style="font-size:.82rem;">{{ enrollee.phone_number }}</td>
                        <td class="py-3">
                          <span class="rel-chip">{{ enrollee.sector }}</span>
                        </td>
                        <td class="py-3">
                          <router-link :to="{ path: '/client/' + enrollee.id }">
                            <button type="button" class="btn btn-sm btn-outline-primary">
                              <i class="fe fe-eye"></i>
                            </button>
                          </router-link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- ── Facility Details Sidebar ──────────────────── -->
            <div class="col-lg-4 col-md-4">
              <div class="card shadow table-card mb-4">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-home" style="font-size:12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Facility Details</h6>
                </div>
                <ul class="list-unstyled mb-0">
                  <li class="info-row">
                    <i class="fe fe-activity info-icon"></i>
                    <div>
                      <p class="info-label">Status</p>
                      <span v-if="provider.status == true" class="status-badge status-badge--approved">Approved</span>
                      <span v-if="provider.status == false" class="status-badge status-badge--pending">Pending</span>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-users info-icon"></i>
                    <div>
                      <p class="info-label">Beneficiaries Enrolled</p>
                      <p class="info-value">{{ providerclients.length }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-user info-icon"></i>
                    <div>
                      <p class="info-label">Contact Name</p>
                      <p class="info-value">{{ provider.firstname }} {{ provider.lastname }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-mail info-icon"></i>
                    <div>
                      <p class="info-label">Email</p>
                      <p class="info-value">{{ provider.email }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-phone info-icon"></i>
                    <div>
                      <p class="info-label">Phone Number</p>
                      <p class="info-value">{{ provider.phone_number }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-home info-icon"></i>
                    <div>
                      <p class="info-label">Facility Type</p>
                      <p class="info-value">{{ provider.phc_general }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-map-pin info-icon"></i>
                    <div>
                      <p class="info-label">LGA / Ward</p>
                      <p class="info-value">
                        <span v-if="provider.user && provider.user.localgovt">{{ provider.user.localgovt.local_name }} / </span>
                        <span v-if="provider.user && provider.user.ward">{{ provider.user.ward.ward_name }}</span>
                      </p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-navigation info-icon"></i>
                    <div>
                      <p class="info-label">Address</p>
                      <p class="info-value">{{ provider.address1 }}</p>
                    </div>
                  </li>
                  <li class="info-row">
                    <i class="fe fe-credit-card info-icon"></i>
                    <div>
                      <p class="info-label">Bank Name</p>
                      <p class="info-value">
                        SafeHaven Microfinance Bank
                        <span v-if="wallet && wallet.account">— {{ wallet.account.accountName }}</span>
                      </p>
                    </div>
                  </li>
                  <li class="info-row" v-if="wallet && wallet.account">
                    <i class="fe fe-hash info-icon"></i>
                    <div>
                      <p class="info-label">Account Number</p>
                      <p class="info-value">{{ wallet.account.accountNumber }}</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- ── Reviews Modal ───────────────────────────────── -->
          <div class="modal fade modal-slide-right" id="slideRightModalAll" tabindex="-1"
            role="dialog" aria-labelledby="slideRightModalLabel" aria-hidden="true">
            <div class="modal-dialog" role="document">
              <div class="modal-content" id="printDiv" ref="printNow">
                <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f, #2563eb);">
                  <div class="d-flex align-items-center">
                    <div class="header-icon-wrap mr-3" style="width:36px;height:36px;">
                      <i class="fe fe-message-circle" style="font-size:16px;color:#fff;"></i>
                    </div>
                    <span class="h6 mb-0 font-weight-bold text-white" id="slideRightModalLabel">Facility Reviews</span>
                  </div>
                  <button type="button" class="close text-white ml-auto" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div class="modal-body p-3">
                  <div class="mb-3" v-if="summary">
                    <p class="font-weight-bold text-dark mb-1" style="font-size:.85rem;">
                      <i class="fe fe-cpu mr-1 text-primary"></i> AI Summary
                    </p>
                    <p class="text-secondary small">{{ summary }}</p>
                  </div>

                  <div v-for="comment in comments" :key="comment" class="mb-3 p-3"
                    style="background:#f8f9fa;border-radius:8px;border-left:3px solid #2563eb;">
                    <div class="d-flex align-items-start justify-content-between mb-1">
                      <p class="mb-0" style="font-size:.88rem;">{{ comment.body }}</p>
                      <div class="ml-2 flex-shrink-0">
                        <span v-if="comment.sentiment == 'Neutral'" class="status-badge status-badge--vetted" style="font-size:.7rem;">Neutral</span>
                        <span v-if="comment.sentiment == 'Positive'" class="status-badge status-badge--approved" style="font-size:.7rem;">Positive</span>
                        <span v-if="comment.sentiment == 'Negative'" class="status-badge status-badge--rejected" style="font-size:.7rem;">Negative</span>
                      </div>
                    </div>
                    <div class="d-flex align-items-center justify-content-between">
                      <small class="text-muted">
                        {{ comment.created_at | moment("from", "now") }}
                        <span v-if="comment.user">&middot; {{ comment.user.firstname }} {{ comment.user.lastname }}</span>
                      </small>
                      <div class="d-flex align-items-center" style="gap:6px;">
                        <star-rating v-if="comment.health_record && comment.health_record.rating"
                          star-size="12" :rating="comment.health_record.rating.score"
                          :show-rating="false" read-only="true" active-color="#198754" />
                        <a v-if="comment.health_record" class="btn btn-sm btn-outline-secondary py-0 px-2"
                          target="_blank" :href="`/encounter/${comment.health_record.id}`">
                          <i class="mdi mdi-medical-bag"></i>
                        </a>
                      </div>
                    </div>
                  </div>

                  <div v-if="!comments || comments.length === 0" class="text-center text-muted py-4">
                    <i class="fe fe-message-circle" style="font-size:2rem;display:block;margin-bottom:8px;"></i>
                    No reviews yet
                  </div>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-sm btn-outline-secondary" data-dismiss="modal">Close</button>
                </div>
              </div>
            </div>
          </div>

        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </div>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import FacilityAnalytics from "@/views/analytics/components/FacilityAnalytics.vue";
import SpecializationsCard from "@/views/telemedicine/SpecializationsCard.vue";
// Import component
import Loading from "vue-loading-overlay";
// Import stylesheet
import "vue-loading-overlay/dist/vue-loading.css";
// Init plugin
import StarRating from "vue-star-rating";

export default {
  components: { FacilityAnalytics,
    SpecializationsCard,
    Navbar,
    Loading,
    StarRating,
  },
  data() {
    return {
      canSeeAnalytics: (function () { const u = JSON.parse(localStorage.getItem("user") || "{}") || {}; return ["shis", "employee", "provider", "provider_employee"].includes(u.type); })(),
      user: null,
      comments: "",
      singleprovider: "",
      providerclients: "",
      summary: "",
      dashboarddata: "",
      wallet: {
        account: "",
      },
      edit: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/detailedProviderHmo/${this.$route.params.id}`)
      .then((response) => {
        this.singleprovider = response.data.data;
        console.log(response);
      })
      .catch((error) => {
        console.error(error);
      });
  },
  methods: {
    dynamicClass() {
      return {
        active: this.isActive,
        disabled: this.isDisabled,
        highlighted: this.isHighlighted,
      };
    },
    getData() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/provider-dashboard-data/${this.$route.params.id}`)
        .then((response) => {
          this.dashboarddata = response.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    getProvider() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/detailedProviderHmo/${this.$route.params.id}`)
        .then((response) => {
          this.singleprovider = response.data.data;
          this.getWallet(this.singleprovider[0]);
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getProviderClients() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/getProviderToUser/${this.$route.params.id}`)
        .then((response) => {
          this.providerclients = response.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getComments() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/comments`, {
          params: {
            paginate_value: 30,
            provider_id: this.$route.params.id,
          },
        })
        .then((response) => {
          this.comments = response.data.data;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    getAIComments() {
      this.axios
        .post(`https://api-aicomments.hayokmedicare.ng/analyze_sentiments`, {
          comments: this.comments,
        })
        .then((response) => {
          this.comments = response.data.comments;
          this.summary = response.data.summary;
          console.log(response);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    acceptProvider(status) {
      if (confirm("Are You Sure You Want to Update Provider?")) {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/approveDisapproveProviderByAgency", {
            agency_id: 90,
            provider_id: this.$route.params.id,
            status: status,
          })
          .then((response) => {
            console.log(response);
            this.$breadstick.notify("Provider Updated!", {
              position: "top-right",
            });
            this.isLoading = false;
            this.getProvider();
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
          });
      }
    },
    rejectProvider() {
      if (confirm("Are You Sure You Want to Disapprove this Provider?")) {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        this.axios
          .post("/api/v1/auth/approveDisapproveProviderByAgency", {
            agency_id: this.user.id,
            provider_id: this.$route.params.id,
            status: 0,
          })
          .then((response) => {
            console.log(response);
            this.$breadstick.notify("Provider Disapproved!", {
              position: "top-right",
            });
            this.isLoading = false;
            this.getProvider();
          })
          .catch((error) => {
            console.log(error.response);
            this.isLoading = false;
          });
      }
    },
    clearIt() {
      this.agency_id = "";
    },
    getWallet(enrollee) {
      this.isLoading = true;
      this.axios
        .get(`/api/v1/auth/wallet/${enrollee.virtual_account}`)
        .then((response) => {
          console.log(response);
          this.wallet = response.data.data;
          this.$toasted.success("Wallet Fetched", {
            position: "top-center",
            duration: 3000,
          });
          this.isLoading = false;
        })
        .catch((error) => {
          console.error(error);
          // this.$toasted.error("Error!", {
          //   position: "top-center",
          //   duration: 3000,
          // });
          this.isLoading = false;
        });
    },
  },
  created() {
    this.getProvider();
    this.getProviderClients();
    this.getData();
    this.getComments();
  },
};
</script>

<style scoped>
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
    padding-bottom: 60px;
  }

  .container.mt-n3 {
    margin-top: -32px !important;
  }

  .header-icon-wrap {
    width: 52px;
    height: 52px;
    background: rgba(255, 255, 255, 0.18);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .header-id-badge {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    font-size: 0.78rem;
    padding: 3px 10px;
    border-radius: 20px;
    font-weight: 500;
  }

  .header-status {
    display: inline-flex;
    align-items: center;
    font-size: 0.78rem;
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
  }

  .header-status--active { background: rgba(16, 185, 129, 0.25); color: #6ee7b7; }
  .header-status--inactive { background: rgba(245, 158, 11, 0.25); color: #fcd34d; }

  .action-card {
    border-radius: 14px !important;
    border: none !important;
  }

  .table-card {
    border-radius: 14px !important;
    border: none !important;
    overflow: hidden;
  }

  .table-head {
    background: #f8fafc;
  }

  .table-th {
    font-size: 0.75rem !important;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b !important;
    font-weight: 600 !important;
    border-bottom: 2px solid #e2e8f0 !important;
    padding: 12px 16px !important;
    white-space: nowrap;
  }

  .table-row {
    border-color: #f1f5f9 !important;
    transition: background 0.15s;
  }

  .table-row:hover {
    background: #f8fafc !important;
  }

  .form-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
  }

  .section-number {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 20px;
    list-style: none;
    border-bottom: 1px solid #f1f5f9;
  }

  .info-row:last-child { border-bottom: none; }

  .info-icon {
    color: #2563eb;
    font-size: 0.95rem;
    margin-top: 2px;
    flex-shrink: 0;
  }

  .info-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    margin-bottom: 1px;
    font-weight: 600;
  }

  .info-value {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.9rem;
    margin-bottom: 0;
  }

  .code-chip {
    display: inline-block;
    background: #eff6ff;
    color: #2563eb;
    font-size: 0.78rem;
    padding: 2px 8px;
    border-radius: 6px;
    font-family: monospace;
  }

  .rel-chip {
    display: inline-block;
    background: #eff6ff;
    color: #2563eb;
    font-size: 0.7rem;
    padding: 1px 7px;
    border-radius: 10px;
    font-weight: 600;
  }

  .status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .status-badge--pending  { background: #fff7ed; color: #c2410c; }
  .status-badge--approved { background: #f0fdf4; color: #15803d; }
  .status-badge--rejected { background: #fef2f2; color: #b91c1c; }
  .status-badge--vetted   { background: #eff6ff; color: #1d4ed8; }
</style>
